/**
 * How the user wants to be addressed in Spanish — `tú`, `vos` or `usted`.
 *
 * WHY THIS IS A STORED PREFERENCE AND NOT AN INFERENCE. The three are not interchangeable registers
 * of one language, they are three different second-person conjugations, and which one is correct is
 * a fact about the PERSON, not about their country: `vos` is the everyday form in El Salvador while
 * `usted` is what the same Salvadoran lawyer expects from a professional tool, and both are used by
 * people in the same office. Guessing from the jurisdiction gets it wrong for half of them, and
 * guessing from the user's own message only works after they have written a second-person sentence,
 * which in a legal question they usually never do.
 *
 * IT IS ALSO CONVERSATION-STABLE, which is what makes it admissible in an ADK system instruction at
 * all (services/agent-web-chat/AGENTS.md): it flips only when the user edits their profile, never per turn, so it costs
 * the prompt prefix nothing. See `render_workspace_block` in `services/agent-web-chat`.
 *
 * SPANISH ONLY, DELIBERATELY. English has one second person, so the preference is inert there — the
 * agent is told the second-person form applies to Spanish replies and nothing else. The reply LANGUAGE is
 * a separate axis and stays where it is (the user's most recent message decides it).
 */
export const SECOND_PERSON_FORMS = ['tu', 'vos', 'usted'] as const

export type SecondPerson = (typeof SECOND_PERSON_FORMS)[number]

/**
 * What an unset preference means. Every row predating the column gets this from the database
 * default, so it is the register the whole existing user base is addressed in on day one — chosen
 * over `usted` deliberately: Marcus is meant to read as a colleague, not as a notary's letter.
 */
export const DEFAULT_SECOND_PERSON: SecondPerson = 'tu'

export function isSecondPerson(value: unknown): value is SecondPerson {
  return typeof value === 'string' && (SECOND_PERSON_FORMS as readonly string[]).includes(value)
}

/** Coerce anything (a legacy null, a hand-edited row, a client that sent nonsense) to a usable form. */
export function toSecondPerson(value: unknown): SecondPerson {
  return isSecondPerson(value) ? value : DEFAULT_SECOND_PERSON
}
